import {emotionStyled} from '@packages/utilities/styled';
import {tokens} from '../../themes';
import {hoverSelector} from '../../utils';
import {IconButton} from '../IconButton';

const backgroundColorPressed = '--Tag-background-pressed';
const borderColor = '--Tag-border-color';
const hoverBackgroundColor = '--Tag-background-hover';
const hoverTextColor = '--Tag-X-button-text-color-hover';

const MEDIUM_COMPACT_HEIGHT = 16;
const MEDIUM_COMFORTABLE_HEIGHT = 24;
const X_LARGE_COMPACT_HEIGHT = 20;
const X_LARGE_COMFORTABLE_HEIGHT = 32;

export const TagCloseButtonView = emotionStyled(IconButton)(
  {
    position: 'relative',
    height: '100%',
    borderRadius: '$0 $1 $1 $0',
    boxShadow: 'none',
    '&:focus-visible': {
      outline: `1px solid ${tokens.colors.blueBorder}`,
      outlineOffset: '1px',
    },
    [hoverSelector]: {
      background: `var(${hoverBackgroundColor})`,
      color: `var(${hoverTextColor})`,
      cursor: 'pointer',
    },
    '&:before': {
      top: 0,
      bottom: 0,
      left: 0,
      width: 1,
      content: '""',
      backgroundColor: `var(${borderColor})`,
      position: 'absolute',
    },
  },
  {
    variants: {
      colorVariant: {
        default: {
          color: '$textSecondary',
          background: '$grayTransparent',
          [backgroundColorPressed]: tokens.colors.backgroundInverse,
          [borderColor]: tokens.colors.backgroundRaised,
          [hoverTextColor]: tokens.colors.textPrimary,
          [hoverBackgroundColor]: tokens.colors.actionSecondaryBackgroundHover,
        },
        primary: {
          color: '$blueText',
          background: '$blueTransparent',
          [backgroundColorPressed]: tokens.colors.blueBackgroundPressed,
          [borderColor]: tokens.colors.blueTransparent,
          [hoverTextColor]: tokens.colors.blueText,
          [hoverBackgroundColor]: tokens.colors.blueTransparentHover,
        },
        green: {
          color: '$greenText',
          background: '$greenTransparent',
          [backgroundColorPressed]: tokens.colors.greenBackgroundPressed,
          [borderColor]: tokens.colors.greenTransparent,
          [hoverTextColor]: tokens.colors.greenText,
          [hoverBackgroundColor]: tokens.colors.greenTransparentHover,
        },
        orange: {
          color: '$orangeText',
          background: '$orangeTransparent',
          [backgroundColorPressed]: tokens.colors.orangeBackgroundPressed,
          [borderColor]: tokens.colors.orangeTransparent,
          [hoverTextColor]: tokens.colors.orangeText,
          [hoverBackgroundColor]: tokens.colors.orangeTransparentHover,
        },
        purple: {
          color: '$purpleText',
          background: '$purpleTransparent',
          [backgroundColorPressed]: tokens.colors.purpleBackgroundPressed,
          [borderColor]: tokens.colors.purpleTransparent,
          [hoverTextColor]: tokens.colors.purpleText,
          [hoverBackgroundColor]: tokens.colors.purpleTransparentHover,
        },
        pink: {
          color: '$pinkText',
          background: '$pinkTransparent',
          [backgroundColorPressed]: tokens.colors.pinkBackgroundPressed,
          [borderColor]: tokens.colors.pinkTransparent,
          [hoverTextColor]: tokens.colors.pinkText,
          [hoverBackgroundColor]: tokens.colors.pinkTransparentHover,
        },
        red: {
          color: '$redText',
          background: '$redTransparent',
          [backgroundColorPressed]: tokens.colors.redBackgroundPressed,
          [borderColor]: tokens.colors.redTransparent,
          [hoverTextColor]: tokens.colors.redText,
          [hoverBackgroundColor]: tokens.colors.redTransparentHover,
        },
        yellow: {
          color: '$yellowText',
          background: '$yellowTransparent',
          [backgroundColorPressed]: tokens.colors.yellowBackgroundPressed,
          [borderColor]: tokens.colors.yellowTransparent,
          [hoverTextColor]: tokens.colors.yellowText,
          [hoverBackgroundColor]: tokens.colors.yellowTransparentHover,
        },
      },
      density: {
        comfortable: {},
        compact: {},
      },
      isBranded: {
        true: {
          height: 'auto',
          width: 'auto',
          borderRadius: '$0 $2 $2 $0',
        },
      },
      isRounded: {
        true: {
          borderTopRightRadius: 9999,
          borderBottomRightRadius: 9999,
        },
      },
      size: {
        medium: {
          [hoverSelector]: {
            // All interactive content in Designer uses `cursor: default`
            cursor: 'default',
          },
        },
        'x-large': {},
      },
      variant: {
        default: {},
        solid: {
          [borderColor]: tokens.colors.whiteA10,
        },
      },
    },
    compoundVariants: [
      {
        conditions: {
          colorVariant: 'default',
          variant: 'solid',
        },
        css: {
          color: '$black',
          background: '$uiBadgeBackground',
          [backgroundColorPressed]: tokens.colors.uiBadgeBackgroundPressed,
          [borderColor]: tokens.colors.blackA10,
          [hoverTextColor]: tokens.colors.black,
          [hoverBackgroundColor]: tokens.colors.uiBadgeBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'primary',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$blueBackground',
          [backgroundColorPressed]: tokens.colors.blueBackgroundPressed,
          [hoverTextColor]: tokens.colors.white,
          [hoverBackgroundColor]: tokens.colors.blueBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'green',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$greenBackground',
          [backgroundColorPressed]: tokens.colors.greenBackgroundPressed,
          [hoverTextColor]: tokens.colors.white,
          [hoverBackgroundColor]: tokens.colors.greenBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'orange',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$orangeBackground',
          [backgroundColorPressed]: tokens.colors.orangeBackgroundPressed,
          [hoverTextColor]: tokens.colors.white,
          [hoverBackgroundColor]: tokens.colors.orangeBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'purple',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$purpleBackground',
          [backgroundColorPressed]: tokens.colors.purpleBackgroundPressed,
          [hoverTextColor]: tokens.colors.white,
          [hoverBackgroundColor]: tokens.colors.purpleBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'pink',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$pinkBackground',
          [backgroundColorPressed]: tokens.colors.pinkBackgroundPressed,
          [hoverTextColor]: tokens.colors.white,
          [hoverBackgroundColor]: tokens.colors.pinkBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'red',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$redBackground',
          [backgroundColorPressed]: tokens.colors.redBackgroundPressed,
          [hoverTextColor]: tokens.colors.white,
          [hoverBackgroundColor]: tokens.colors.redBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'yellow',
          variant: 'solid',
        },
        css: {
          color: '$black',
          background: '$yellowBackground',
          [backgroundColorPressed]: tokens.colors.yellowBackgroundPressed,
          [borderColor]: tokens.colors.blackA10,
          [hoverTextColor]: tokens.colors.black,
          [hoverBackgroundColor]: tokens.colors.yellowBackgroundHover,
        },
      },
      {
        conditions: {
          size: 'medium',
          density: 'compact',
        },
        css: {
          height: MEDIUM_COMPACT_HEIGHT,
          width: MEDIUM_COMPACT_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'medium',
          density: 'compact',
          isRounded: true,
        },
        css: {
          height: MEDIUM_COMPACT_HEIGHT,
          width: MEDIUM_COMPACT_HEIGHT,
          svg: {
            paddingRight: '$0.5',
          },
        },
      },
      {
        conditions: {
          size: 'medium',
          density: 'comfortable',
        },
        css: {
          height: MEDIUM_COMFORTABLE_HEIGHT,
          width: MEDIUM_COMFORTABLE_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'medium',
          density: 'comfortable',
          isRounded: true,
        },
        css: {
          height: MEDIUM_COMFORTABLE_HEIGHT,
          width: MEDIUM_COMFORTABLE_HEIGHT,
          svg: {
            paddingRight: '$0.5',
          },
        },
      },
      {
        conditions: {
          size: 'x-large',
          density: 'compact',
        },
        css: {
          height: X_LARGE_COMPACT_HEIGHT,
          width: X_LARGE_COMPACT_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'x-large',
          density: 'compact',
          isRounded: true,
        },
        css: {
          height: X_LARGE_COMPACT_HEIGHT,
          width: X_LARGE_COMPACT_HEIGHT,
          svg: {
            paddingRight: '$0.5',
          },
        },
      },
      {
        conditions: {
          size: 'x-large',
          density: 'comfortable',
        },
        css: {
          height: X_LARGE_COMFORTABLE_HEIGHT,
          width: X_LARGE_COMFORTABLE_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'x-large',
          density: 'comfortable',
          isRounded: true,
        },
        css: {
          height: X_LARGE_COMFORTABLE_HEIGHT,
          width: X_LARGE_COMFORTABLE_HEIGHT,
          paddingRight: '$2',
        },
      },
      {
        conditions: {
          isBranded: true,
          density: 'compact',
        },
        css: {
          padding: '$2',
          height: 'auto',
          width: 'auto',
        },
      },
      {
        conditions: {
          isBranded: true,
          density: 'comfortable',
        },
        css: {
          padding: '$3',
          height: 'auto',
          width: 'auto',
        },
      },
    ],
    defaultVariants: {
      colorVariant: 'default',
      variant: 'default',
    },
  }
);

export const TagContentView = emotionStyled('span')(
  {
    alignContent: 'center',
    borderRadius: '$1',
    border: 'none',
    minWidth: 0,
    textDecoration: 'none',
  },
  {
    componentName: 'Tag',
    variants: {
      colorVariant: {
        default: {
          color: '$textSecondary',
          background: '$grayTransparent',
          [backgroundColorPressed]: tokens.colors.backgroundInverse,
          [hoverBackgroundColor]: tokens.colors.actionSecondaryBackgroundHover,
        },
        primary: {
          color: '$blueText',
          background: '$blueTransparent',
          [backgroundColorPressed]: tokens.colors.blueBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.blueTransparentHover,
        },
        green: {
          color: '$greenText',
          background: '$greenTransparent',
          [backgroundColorPressed]: tokens.colors.greenBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.greenTransparentHover,
        },
        orange: {
          color: '$orangeText',
          background: '$orangeTransparent',
          [backgroundColorPressed]: tokens.colors.orangeBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.orangeTransparentHover,
        },
        purple: {
          color: '$purpleText',
          background: '$purpleTransparent',
          [backgroundColorPressed]: tokens.colors.purpleBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.purpleTransparentHover,
        },
        pink: {
          color: '$pinkText',
          background: '$pinkTransparent',
          [backgroundColorPressed]: tokens.colors.pinkBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.pinkTransparentHover,
        },
        red: {
          color: '$redText',
          background: '$redTransparent',
          [backgroundColorPressed]: tokens.colors.redBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.redTransparentHover,
        },
        yellow: {
          color: '$yellowText',
          background: '$yellowTransparent',
          [backgroundColorPressed]: tokens.colors.yellowBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.yellowTransparentHover,
        },
      },
      density: {
        comfortable: {},
        compact: {},
      },
      isBranded: {
        true: {
          fontFamily: '$brandedBody',
          fontSize: '$brandedBody',
          lineHeight: '$brandedHelpText',
          borderRadius: '$2',
        },
      },
      isDisabled: {
        true: {
          cursor: 'not-allowed',
          opacity: tokens.opacities.disabled,
        },
      },
      isDismissable: {
        true: {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        },
      },
      isRounded: {
        true: {
          borderRadius: 9999,
        },
      },
      isInteractive: {
        true: {
          '&:focus-visible': {
            outline: `1px solid ${tokens.colors.blueBorder}`,
            outlineOffset: '1px',
          },
          [hoverSelector]: {
            background: `var(${hoverBackgroundColor})`,
            cursor: 'pointer',
          },
        },
      },
      size: {
        medium: {},
        'x-large': {},
      },
      variant: {
        default: {},
        solid: {},
      },
    },
    compoundVariants: [
      {
        conditions: {
          colorVariant: 'default',
          variant: 'solid',
        },
        css: {
          color: '$black',
          background: '$uiBadgeBackground',
          [backgroundColorPressed]: tokens.colors.uiBadgeBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.uiBadgeBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'primary',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$blueBackground',
          [backgroundColorPressed]: tokens.colors.blueBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.blueBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'green',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$greenBackground',
          [backgroundColorPressed]: tokens.colors.greenBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.greenBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'orange',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$orangeBackground',
          [backgroundColorPressed]: tokens.colors.orangeBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.orangeBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'purple',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$purpleBackground',
          [backgroundColorPressed]: tokens.colors.purpleBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.purpleBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'pink',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$pinkBackground',
          [backgroundColorPressed]: tokens.colors.pinkBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.pinkBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'red',
          variant: 'solid',
        },
        css: {
          color: '$white',
          background: '$redBackground',
          [backgroundColorPressed]: tokens.colors.redBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.redBackgroundHover,
        },
      },
      {
        conditions: {
          colorVariant: 'yellow',
          variant: 'solid',
        },
        css: {
          color: '$black',
          background: '$yellowBackground',
          [backgroundColorPressed]: tokens.colors.yellowBackgroundPressed,
          [hoverBackgroundColor]: tokens.colors.yellowBackgroundHover,
        },
      },
      {
        conditions: {
          isInteractive: true,
          size: 'medium',
        },
        css: {
          [hoverSelector]: {
            // All interactive content in Designer uses `cursor: default`
            cursor: 'default',
          },
        },
      },
      {
        conditions: {
          isDismissable: true,
          isRounded: true,
        },
        css: {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        },
      },
      {
        conditions: {
          size: 'medium',
          density: 'compact',
        },
        css: {
          padding: '$0 $0.5',
          height: MEDIUM_COMPACT_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'medium',
          density: 'compact',
          isRounded: true,
        },
        css: {
          padding: '$0 $1',
          height: MEDIUM_COMPACT_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'medium',
          density: 'compact',
          isDismissable: true,
        },
        css: {
          padding: '$0 $1 $0 $0.5',
          height: MEDIUM_COMPACT_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'medium',
          density: 'compact',
          isDismissable: true,
          isRounded: true,
        },
        css: {
          padding: '$0 $1 $0 $1',
          height: MEDIUM_COMPACT_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'medium',
          density: 'comfortable',
        },
        css: {
          padding: '$1 $2',
          height: MEDIUM_COMFORTABLE_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'medium',
          density: 'comfortable',
          isDismissable: true,
        },
        css: {
          padding: '$1 $1 $1 $2',
          height: MEDIUM_COMFORTABLE_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'x-large',
          density: 'compact',
        },
        css: {
          padding: '$0 $1',
          height: X_LARGE_COMPACT_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'x-large',
          density: 'compact',
          isRounded: true,
        },
        css: {
          padding: '$0 $2',
          height: X_LARGE_COMPACT_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'x-large',
          density: 'compact',
          isDismissable: true,
        },
        css: {
          padding: '$0 $2 $0 $1',
          height: X_LARGE_COMPACT_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'x-large',
          density: 'compact',
          isDismissable: true,
          isRounded: true,
        },
        css: {
          padding: '$0 $2 $0 6px',
          height: X_LARGE_COMPACT_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'x-large',
          density: 'comfortable',
        },
        css: {
          padding: '$0 $3',
          height: X_LARGE_COMFORTABLE_HEIGHT,
        },
      },
      {
        conditions: {
          size: 'x-large',
          density: 'comfortable',
          isDismissable: true,
        },
        css: {
          padding: '$0 $2 $0 $3',
          height: X_LARGE_COMFORTABLE_HEIGHT,
        },
      },
      {
        conditions: {
          isBranded: true,
          density: 'compact',
        },
        css: {
          padding: '$2 $3',
          height: 'auto',
        },
      },
      {
        conditions: {
          isBranded: true,
          density: 'comfortable',
        },
        css: {
          padding: '$3 $4',
          height: 'auto',
        },
      },
    ],
    defaultVariants: {
      colorVariant: 'default',
      variant: 'default',
    },
  }
);

export const TagWrapperView = emotionStyled('div')(
  {
    display: 'inline-flex',
    position: 'relative',
    width: 'fit-content',
    maxWidth: '100%',
    borderRadius: '$1',
  },
  {
    variants: {
      isBranded: {
        true: {
          borderRadius: '$2',
        },
      },
      isRounded: {
        true: {
          borderRadius: 9999,
        },
      },
    },
  }
);
